import React, { useEffect, useRef } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { useGetBestReviews } from 'shared/api/query';
import { useDragScroll, useLangNavigate, useRouteChangeHandler } from 'shared/hooks';

import { ReviewCard } from './ReviewCard';

export function Reviews() {
  const { t } = useTranslation('home');
  const { data } = useGetBestReviews('ALL', { page: 0, size: 10 });
  const { navigateWithLang } = useLangNavigate();
  const { handleRouteChange } = useRouteChangeHandler();

  const mobileReviews = data?.reviewMainInfos.slice(0, 6) ?? [];
  const tripledReviews = [...mobileReviews, ...mobileReviews, ...mobileReviews];
  const scrollRef = useRef<HTMLDivElement>(null);

  // 태블릿 밴드(800~1279px)의 마우스 사용자용 — 스크롤바가 숨겨진 스냅 캐러셀은
  // 터치/트랙패드로만 넘길 수 있어 마우스 드래그 스크롤을 부여한다
  useDragScroll(scrollRef);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || mobileReviews.length === 0) return;

    el.scrollLeft = el.scrollWidth / 3;

    let timer: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const oneThird = el.scrollWidth / 3;
        if (el.scrollLeft < oneThird * 0.5) {
          el.scrollLeft += oneThird;
        } else if (el.scrollLeft > oneThird * 2) {
          el.scrollLeft -= oneThird;
        }
      }, 150);
    };

    el.addEventListener('scroll', handleScroll);
    return () => {
      el.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, [mobileReviews.length]);

  return (
    <section
      id="reviews-section"
      className="flex flex-col items-center gap-6 px-5 py-10 md:gap-[55px] md:px-4 md:py-[120px]"
    >
      <div className="flex w-full flex-row items-center gap-3 md:w-auto md:flex-col md:gap-[7px]">
        <img
          src="/images/optimized/illusts/il_bookingguide_note.webp"
          alt="booking guide"
          className="h-10 w-10 shrink-0 object-cover md:h-[82px] md:w-[82px]"
          width={82}
          height={82}
        />
        <div className="flex flex-1 flex-col md:flex-none md:items-center md:gap-[4px]">
          <h2 className="text-left text-[15px] font-semibold leading-[1.4] tracking-[-0.022em] text-[#222222] md:text-center md:text-[24px] md:leading-[1.5]">
            <Trans i18nKey="home:reviews.title" components={{ br: <br /> }} />
          </h2>
        </div>
      </div>

      {/* Mobile: horizontal infinite snap scroll */}
      <div
        ref={scrollRef}
        className="-mx-5 flex w-screen cursor-grab snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] active:cursor-grabbing md:hidden [&::-webkit-scrollbar]:hidden"
      >
        {tripledReviews.map((review, index) => (
          <div key={`${review.reviewId}-${index}`} className="w-[85%] shrink-0 snap-center min-[500px]:w-[42%]">
            <ReviewCard review={review} />
          </div>
        ))}
      </div>

      {/* Desktop: 2x3 grid */}
      <div className="hidden flex-col gap-[28px] md:flex">
        <div className="flex gap-[28px]">
          {data?.reviewMainInfos.slice(0, 3).map((review) => <ReviewCard key={review.reviewId} review={review} />)}
        </div>

        <div className="flex gap-[28px]">
          {data?.reviewMainInfos.slice(3, 6).map((review) => <ReviewCard key={review.reviewId} review={review} />)}
        </div>
      </div>

      <div className="flex w-full flex-col gap-2 md:mt-[25px] md:w-auto md:flex-row md:gap-[16px]">
        <button
          onClick={() => {
            window.dataLayer?.push({ event: 'click_main', menu: 'explore_all_services', section: 'Reviews' });
            handleRouteChange('/all-services');
          }}
          className="flex items-center justify-center gap-7 rounded-[8px] border border-[#FF2D2E] bg-white px-5 py-3 text-sm font-semibold leading-[1.5] tracking-[-0.022em] text-primary transition-opacity hover:opacity-90 md:px-[24px] md:py-[12px] md:text-[16px]"
        >
          <span>{t('reviews.exploreServices')}</span>
        </button>

        <button
          onClick={() => {
            window.dataLayer?.push({ event: 'click_main', menu: 'start_with_housing', section: 'Reviews' });
            navigateWithLang('/listing');
          }}
          className="flex items-center justify-center gap-7 rounded-[8px] bg-[#FF4444] px-5 py-3 text-sm font-semibold leading-[1.5] tracking-[-0.022em] text-white transition-opacity hover:opacity-90 md:px-[24px] md:py-[12px] md:text-[16px]"
        >
          <span>{t('reviews.startWithHousing')}</span>
        </button>
      </div>
    </section>
  );
}
